import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { uiApi } from '@/api/ui.api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { queryKeys, tierOptions } from '@/lib/query';
import type { UiScreenActions } from '@/types/ui';
import { isRegisteredScreenPath, resolvePageScreenActionPath } from '@/utils/screenActionResolve';
import {
  canViewModuleSettingsNav,
  isSettingsNavPath,
  moduleSettingsActionGrants,
} from '@/utils/moduleSettingsAccess';
import { useActiveModule } from '@/hooks/useActiveModule';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { moduleApplicationAccessPath } from '@/utils/moduleApplicationAccess';

const DEFAULT_ACTIONS: UiScreenActions = {
  path: '',
  canView: true,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canApprove: false,
  canReject: false,
  canExport: false,
  canImport: false,
  canProcess: false,
};

const DENIED_ACTIONS: UiScreenActions = {
  path: '',
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canApprove: false,
  canReject: false,
  canExport: false,
  canImport: false,
  canProcess: false,
};

export type ResolvedScreenActions = UiScreenActions & { resolved: boolean };

/** Backend-driven screen actions — session cache → API. No client-side permission registry. */
export function useScreenActions(pathname?: string): ResolvedScreenActions {
  const location = useLocation();
  const path = pathname ?? location.pathname;
  const sessionActions = useAppSelector((s) => s.session.screenActions);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const screenGrantsConfigured = useAppSelector((s) => s.session.screenGrantsConfigured);
  const screenGrants = useAppSelector((s) => s.session.screenGrants);
  const activeModule = useActiveModule();

  const visiblePaths = useMemo(
    () => (sessionActions != null ? Object.keys(sessionActions) : []),
    [sessionActions],
  );

  const cachedPath = useMemo(
    () => resolvePageScreenActionPath(path, visiblePaths),
    [path, visiblePaths],
  );
  const cached = cachedPath ? sessionActions?.[cachedPath] : undefined;

  const { data: fetched } = useQuery({
    queryKey: queryKeys.ui.screenActions(path),
    queryFn: () => uiApi.screenActions(path),
    enabled: sessionLoaded && cached === undefined && !(screenGrantsConfigured && isRegisteredScreenPath(path)),
    ...tierOptions('operational'),
  });

  return useMemo(() => {
    if (
      sessionLoaded &&
      screenGrantsConfigured &&
      activeModule &&
      isSettingsNavPath(path)
    ) {
      const canView =
        canViewModuleSettingsNav(path, activeModule, screenGrants, true) === true;
      const grants = moduleSettingsActionGrants(path.split('?')[0], activeModule, screenGrants);
      return {
        ...(canView ? DEFAULT_ACTIONS : DENIED_ACTIONS),
        canCreate: canView && grants?.create === true,
        canEdit: canView && grants?.edit === true,
        canDelete: canView && grants?.delete === true,
        path,
        resolved: true,
      };
    }

    const backend = cached ?? fetched;
    if (backend) {
      return { ...DEFAULT_ACTIONS, ...backend, path, resolved: true };
    }
    if (
      sessionLoaded &&
      screenGrantsConfigured &&
      activeModule &&
      getModuleEntryByErpModule(activeModule)?.homePath === path.split('?')[0].replace(/\/+$/, '') &&
      screenGrants?.[moduleApplicationAccessPath(activeModule)]?.view === true
    ) {
      return { ...DEFAULT_ACTIONS, canView: true, path, resolved: true };
    }
    if (sessionLoaded && (screenGrantsConfigured || isRegisteredScreenPath(path))) {
      return { ...DENIED_ACTIONS, path, resolved: true };
    }
    return {
      ...DEFAULT_ACTIONS,
      path,
      resolved: false,
    } satisfies ResolvedScreenActions;
  }, [activeModule, cached, fetched, path, screenGrants, screenGrantsConfigured, sessionLoaded]);
}
